/* eslint-disable no-underscore-dangle */
/* eslint-disable no-nested-ternary */
import React, {
  forwardRef,
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  Box,
  Text,
  Stack,
  Button,
  HStack,
  Modal,
  ModalContent,
  BoxProps,
  ButtonProps,
  TextProps,
  StackProps,
  useBreakpointValue,
  InputProps,
} from '@chakra-ui/react';
import dayjs, { Dayjs } from 'dayjs';
import ptBr from 'dayjs/locale/pt-br';
import { useClickAway, useToggle, useWindowScroll } from 'react-use';

import { Icon } from 'components/icon';
import { Input } from 'components/inputs';
import {
  DayType,
  useCalendar,
  UseCalendarType,
  WeekType,
} from 'hooks/useCalendar';
import { useCombinedRefs } from 'hooks/useCombinedRefs';

dayjs.locale(ptBr);
const dayStyles = {
  daySelected: {
    background: 'primary.500',
    color: 'white',
    borderRadius: '50%',
    ':hover': { background: 'primary.500' },
  },
  dayToday: {
    color: 'secondary.200',
    borderRadius: '50%',
  },
  dayDisabled: {
    color: 'gray.500',
    borderRadius: '50%',
  },
  prevMonthDay: {
    color: 'gray.500',
    borderRadius: '50%',
  },
  nextMonthDay: {
    color: 'gray.500',
    borderRadius: '50%',
  },
  day: {
    color: 'black',
    borderRadius: '50%',
    ':hover': { background: 'gray.50' },
  },
};

type CustomStyles = {
  container?: BoxProps;
  datepicker?: BoxProps;
  prevButton?: ButtonProps;
  nextButton?: ButtonProps;
  prevMonthDay?: ButtonProps;
  nextMonthDay?: ButtonProps;
  date?: TextProps;
  weeks?: StackProps;
  weekLabel?: TextProps;
  day?: ButtonProps;
  daySelected?: ButtonProps;
  dayDisabled?: ButtonProps;
  dayToday?: ButtonProps;
};

interface DatePickerProps
  extends Omit<InputProps, 'position' | 'type' | 'defaultValue'>,
    Omit<UseCalendarType, 'onSelect'> {
  minDate?: Dayjs;
  value?: string;
  label?: string;
  tooltip?: string;
  position?: keyof typeof positions;
  isOpen?: boolean;
  setIsOpen?: (_isOpen: boolean) => void;
  onChangeDate?: (_date: Dayjs) => void;
  styles?: CustomStyles;
  boxProps?: BoxProps;
  defaultValue?: string | Date | Dayjs;
}
export type { DatePickerProps };

interface CalendarProps {
  date: Dayjs;
  weeks: WeekType[];
  onNextPage: () => void;
  onPrevPage: () => void;
  onSelect: (_day: DayType) => void;
  styles: CustomStyles;
}

function Calendar({
  date,
  weeks,
  onNextPage,
  onPrevPage,
  onSelect,
  styles,
}: CalendarProps) {
  return (
    <Box
      maxW={800}
      minW={300}
      my={0}
      mx="auto"
      textAlign="center"
      {...styles.datepicker}>
      <Box>
        <HStack
          py={4}
          w="100%"
          justifyContent="space-between"
          alignItems="center">
          <Button
            variant="ghost"
            onClick={onPrevPage}
            {...styles.prevButton}>
            <Icon
              color="primary.500"
              name="long-arrow-left"
              family="far"
              size="md"
            />
          </Button>
          <Text
            variant="XLargeBold"
            {...styles.date}>
            {date.format('MMMM YYYY')}
          </Text>
          <Button
            variant="ghost"
            onClick={onNextPage}
            {...styles.nextButton}>
            <Icon
              color="primary.500"
              name="long-arrow-right"
              family="far"
              size="md"
            />
          </Button>
        </HStack>
        <HStack
          borderBottomWidth={1}
          borderColor="gray.200"
          pb={2}
          mb={2}
          justifyContent="space-between"
          {...styles.weeks}>
          {weeks.map(({ weekday, id }) => (
            <Text
              color="gray.200"
              width="calc(100% / 7)"
              variant="Medium"
              key={weekday + id}
              {...styles.weekLabel}>
              {weekday}
            </Text>
          ))}
        </HStack>
        <Stack>
          {weeks.map((week) => (
            <HStack key={week.id}>
              {week.days.map((day) => {
                const type = day.isSelected
                  ? 'daySelected'
                  : day.isToday
                    ? 'dayToday'
                    : day.isDisabled
                      ? 'dayDisabled'
                      : day.isNextMonth
                        ? 'nextMonthDay'
                        : day.isPrevMonth
                          ? 'prevMonthDay'
                          : 'day';

                return (
                  <Button
                    variant="ghost"
                    w={10}
                    h={10}
                    size="xs"
                    display="inline-block"
                    onClick={() => onSelect(day)}
                    key={day.date.toString()}
                    disabled={day.isDisabled}
                    {...dayStyles[type]}
                    {...styles[type]}>
                    <Text variant="Medium">{day.date.format('DD')}</Text>
                  </Button>
                );
              })}
            </HStack>
          ))}
        </Stack>
      </Box>
    </Box>
  );
}

const positions = {
  bottom: { top: '100%' },
  top: { bottom: '100%' },
  left: { right: '100%' },
  right: { left: '100%' },
  auto: {},
};

export const DatePicker = forwardRef(
  (
    {
      value,
      onChange,
      position = 'bottom',
      isOpen,
      setIsOpen,
      onChangeDate,
      styles = {},
      boxProps,
      defaultValue,
      ...rest
    }: DatePickerProps,
    ref: MutableRefObject<any>,
  ) => {
    const [innerValue, setInnerValue] = useState<Dayjs>(dayjs().startOf('day'));
    const innerRef = useRef(null);
    const [innerIsOpen, setInnerIsOpen] = useToggle(isOpen);
    const { y } = useWindowScroll();
    const isMobile = useBreakpointValue({
      xs: true,
      md: false,
    });
    const combinedRef = useCombinedRefs(ref, innerRef);
    const containerRef = useRef(null);

    const calendar = useCalendar({
      ...rest,
      selected: innerValue,
      onSelect: ({ date }) => {
        const formattedDate = date.format('DD/MM/YYYY');
        Object.getOwnPropertyDescriptor(
          window.HTMLInputElement.prototype,
          'value',
        ).set.call(combinedRef.current, formattedDate);
        combinedRef.current.dispatchEvent(
          new Event('input', { bubbles: true }),
        );
        setInnerValue(date);
        setInnerIsOpen(false);
      },
    });
    const innerPosition = useMemo(() => {
      if (position === 'auto') {
        // eslint-disable-next-line no-unsafe-optional-chaining
        return combinedRef.current?.getBoundingClientRect().top - y < 100
          ? 'bottom'
          : 'top';
      }
      return position;
    }, [position, y]);

    useClickAway(containerRef, () => {
      if (!isMobile) {
        setInnerIsOpen(false);
      }
    });

    useEffect(() => {
      if (isOpen !== innerIsOpen) {
        setInnerIsOpen(isOpen);
      }
    }, [isOpen]);

    useEffect(() => {
      if (typeof defaultValue === 'string') {
        setInnerValue(dayjs(defaultValue, 'DD/MM/YYYY'));
      } else if (defaultValue) {
        setInnerValue(dayjs(defaultValue).startOf('day'));
      }
    }, [defaultValue]);

    useEffect(() => {
      if (isOpen !== innerIsOpen) {
        setIsOpen?.(innerIsOpen);
      }
    }, [innerIsOpen]);
    useEffect(() => {
      if (value && value !== combinedRef.current.value) {
        const asDate = dayjs(value);
        if (asDate.isValid()) {
          combinedRef.current.value = asDate.format('DD/MM/YYYY');
        } else {
          combinedRef.current.value = value;
        }
      }
    }, [value]);

    const _onChange = useCallback(
      (e) => {
        const date = dayjs(e.target.value, 'DD/MM/YYYY');
        const isValid = date.isValid();
        if (isValid) {
          e.target.value = date.toDate();
        }
        onChange?.(e);
        if (!date.isSame(innerValue) && isValid) {
          onChangeDate?.(date);
          setInnerValue(date);
        }
      },
      [innerValue],
    );

    const Container = useCallback(
      ({ isOpen: open, ...modalContentProps }) => {
        if (isMobile) {
          return (
            <Modal
              isOpen={open}
              isCentered
              onClose={() => setInnerIsOpen(false)}>
              <ModalContent
                h="fit-content"
                p={6}
                {...modalContentProps}
              />
            </Modal>
          );
        }
        return (
          <Box
            zIndex={9999999}
            {...positions[innerPosition]}
            hidden={!open}
            bg="white"
            borderWidth={1}
            borderRadius={8}
            w="fit-content"
            shadow="shadow1"
            p={6}
            position="absolute"
            {...modalContentProps}
          />
        );
      },
      [isMobile, innerPosition, positions],
    );
    return (
      <Stack
        w="100%"
        ref={containerRef}
        role="group"
        justifyContent="center"
        position="relative"
        {...boxProps}>
        <Input
          width="100%"
          variant="outline"
          type="text"
          id="datepicker"
          ref={combinedRef}
          onMouseDown={() => setInnerIsOpen(true)}
          onChange={_onChange}
          viewProps={boxProps}
          {...rest}
        />
        <Container
          isOpen={innerIsOpen}
          {...styles.container}>
          <Calendar
            styles={styles}
            {...calendar}
          />
        </Container>
      </Stack>
    );
  },
);
export default DatePicker;
